import { BomItem } from '../bom/bomUtils'
import { PriceMap, ProductPriceNoData, ProductPriceUnavailable } from './ProductPricingContext'
import { Decimal } from 'decimal.js-light'
import { arrayIncludes } from '../utils/arrayUtils'

export interface TotalPriceForBomResult {
  price: Decimal | typeof ProductPriceUnavailable
  isMissingData: boolean
}

export function getTotalPriceForBom(fullListBom: readonly BomItem[], priceCache: PriceMap): TotalPriceForBomResult {
  const isMissingData = arrayIncludes(fullListBom, it => priceCache[it.itemNo] === ProductPriceNoData)

  const price = fullListBom.reduce<Decimal | typeof ProductPriceUnavailable>((accum, curr) => {
    const price = priceCache[curr.itemNo] ?? ProductPriceUnavailable
    if (accum !== ProductPriceUnavailable && price !== ProductPriceUnavailable) {
      return accum.add(price.times(curr.quantity))
    }

    return ProductPriceUnavailable
  }, new Decimal(0))

  return {
    price,
    isMissingData,
  }
}

export function isBulkPackProduct(listItem: BomItem): boolean {
  // product has a pack quantity or included multiple components
  return listItem.packSize > 1 || listItem.components.length > 0
}

export interface ListItemPricing {
  isMissingData: boolean
  price: Decimal | typeof ProductPriceUnavailable
  disclaimer: string | null
}

export function getListItemPricing(listItemBom: readonly BomItem[], priceCache: PriceMap): ListItemPricing {
  // add up prices of components, excluding bulk packs
  const price = listItemBom.reduce<Decimal | typeof ProductPriceUnavailable>((accum, curr) => {
    if (isBulkPackProduct(curr)) {
      return accum
    }

    const price = priceCache[curr.itemNo] ?? ProductPriceUnavailable

    if (accum !== ProductPriceUnavailable && price !== ProductPriceUnavailable) {
      return accum.add(price.times(curr.individualQuantity).dividedBy(curr.packSize))
    }

    return ProductPriceUnavailable
  }, new Decimal(0))

  const isMissingData = arrayIncludes(listItemBom, it => priceCache[it.itemNo] === ProductPriceNoData)

  if (isMissingData) {
    return {
      isMissingData: true,
      price: ProductPriceUnavailable,
      disclaimer: null,
    }
  }

  const hasIncompletePacks = arrayIncludes(listItemBom, it => it.individualQuantity % it.packSize !== 0)

  let disclaimer: string | null = null

  if (price === null) {
    disclaimer = 'Pricing is unavailable for some products'
  } else if (hasIncompletePacks) {
    disclaimer = 'Note: price does not include items sold in bulk packs, which are listed separately below.'
  }

  return {
    isMissingData: false,
    disclaimer,
    price,
  }
}